import { memo, useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, Trash2, CalendarRange, Gauge, AlertTriangle, Clock, Lock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const MON = ['', 'Ian', 'Feb', 'Mar', 'Apr', 'Mai', 'Iun', 'Iul', 'Aug', 'Sep', 'Oct', 'Noi', 'Dec'];
const fmtDay = (d) => (d ? `${d.slice(8, 10)}.${d.slice(5, 7)}` : '—');
const fmtP = (p) => (p && p.start && p.end ? (p.start === p.end ? fmtDay(p.end) : `${fmtDay(p.start)}–${fmtDay(p.end)}`) : '—');
const fmtWhen = (s) => (s ? new Date(s).toLocaleString('ro-RO', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '');

const MotivareImportsManager = memo(function MotivareImportsManager({ open, onClose, year, month, onChanged }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [confirmAll, setConfirmAll] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [confirmFrom, setConfirmFrom] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/motivare/imports`, { ...auth(), params: { year, month } });
      setData(r.data);
    } catch { toast.error('Nu s-au putut încărca încărcările'); }
    finally { setLoading(false); }
  }, [year, month]);

  useEffect(() => { if (open) { setConfirmAll(false); setConfirmFrom(false); setFromDate(''); load(); } }, [open, load]);

  const delItem = async (kind, day) => {
    setBusy(`${kind}-${day}`);
    try {
      await axios.delete(`${API_URL}/motivare/imports/${kind}`, { ...auth(), params: { year, month, day } });
      toast.success(kind === 'activity' ? 'Raport de cantitate șters' : 'Raport Level șters');
      await load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Ștergerea a eșuat'); }
    finally { setBusy(null); }
  };

  const delAll = async () => {
    setBusy('all');
    try {
      await axios.delete(`${API_URL}/motivare/imports/month`, { ...auth(), params: { year, month } });
      toast.success('Toate încărcările lunii au fost șterse');
      await load(); onChanged?.(); setConfirmAll(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Ștergerea a eșuat'); }
    finally { setBusy(null); }
  };

  const delFrom = async () => {
    if (!fromDate) return;
    setBusy('from');
    try {
      const r = await axios.delete(`${API_URL}/motivare/imports/from-date`, { ...auth(), params: { year, month, day: fromDate } });
      const d = r.data || {};
      toast.success(`Șters de la ${fromDate.slice(8, 10)}.${fromDate.slice(5, 7)} încolo (${d.activity_deleted || 0} cantitate, ${d.level_deleted || 0} Level)`);
      await load(); onChanged?.(); setConfirmFrom(false); setFromDate('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Ștergerea a eșuat'); }
    finally { setBusy(null); }
  };

  const monthStr = `${year}-${String(month).padStart(2, '0')}`;

  const locked = data?.locked;
  const acts = data?.activity || [];
  const lvls = data?.level || [];
  const empty = acts.length === 0 && lvls.length === 0;

  const Row = ({ kind, it, icon: Icon, color }) => (
    <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-md border bg-white" data-testid={`import-row-${kind}-${it.day}`}>
      <div className="flex items-center gap-2 min-w-0">
        <Icon className={`w-4 h-4 shrink-0 ${color}`} />
        <div className="min-w-0">
          <div className="text-sm font-medium flex items-center gap-2">
            {kind === 'activity' ? `Perioadă ${fmtP(it.period)}` : `Level până la ${fmtDay(it.day)}`}
            {it.is_current && <span className="text-[10px] px-1.5 rounded bg-emerald-100 text-emerald-700 font-semibold uppercase">activ</span>}
          </div>
          <div className="text-xs text-slate-400 truncate">
            {it.filename ? `${it.filename} · ` : ''}{it.agents_count} agenți{it.imported_at ? ` · ` : ''}
            {it.imported_at && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{fmtWhen(it.imported_at)}</span>}
          </div>
        </div>
      </div>
      <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50 shrink-0"
        disabled={locked || busy === `${kind}-${it.day}`} onClick={() => delItem(kind, it.day)} data-testid={`import-delete-${kind}-${it.day}`}>
        {busy === `${kind}-${it.day}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
      </Button>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="motivare-imports-manager">
        <DialogHeader>
          <DialogTitle>Gestionează încărcările — {MON[month]} {year}</DialogTitle>
          <DialogDescription>Șterge rapoartele greșite (cantitate sau Level). Luna se recalculează automat din ce rămâne. Agenții nu se șterg.</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : (
          <div className="space-y-4">
            {locked && <div className="flex items-center gap-2 text-xs text-slate-600 bg-slate-100 rounded-md px-3 py-2"><Lock className="w-3.5 h-3.5" /> Luna este blocată (istoric) — nu se pot șterge încărcări.</div>}

            {empty ? (
              <p className="text-sm text-slate-400 text-center py-6">Nicio încărcare pentru această lună.</p>
            ) : (
              <>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Rapoarte de cantitate ({acts.length})</div>
                  <div className="space-y-2">
                    {acts.length ? acts.map((it) => <Row key={it.day} kind="activity" it={it} icon={CalendarRange} color="text-blue-600" />)
                      : <p className="text-xs text-slate-400">Niciun raport de cantitate.</p>}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Rapoarte de calitate (Level) ({lvls.length})</div>
                  <div className="space-y-2">
                    {lvls.length ? lvls.map((it) => <Row key={it.day} kind="level" it={it} icon={Gauge} color="text-emerald-600" />)
                      : <p className="text-xs text-slate-400">Niciun raport Level.</p>}
                  </div>
                </div>
              </>
            )}

            {!empty && !locked && (
              <div className="pt-3 border-t space-y-3">
                <div className="flex flex-wrap items-end gap-2">
                  <div>
                    <label className="text-[10px] text-slate-500 block mb-1">Șterge de la o dată încolo</label>
                    <input type="date" className="h-8 rounded-md border bg-white px-2 text-xs" min={`${monthStr}-01`} max={`${monthStr}-31`}
                      value={fromDate} onChange={(e) => { setFromDate(e.target.value); setConfirmFrom(false); }} data-testid="from-date-input" />
                  </div>
                  {confirmFrom ? (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-red-700">Sigur?</span>
                      <Button variant="destructive" size="sm" onClick={delFrom} disabled={busy === 'from'} data-testid="from-date-confirm">
                        {busy === 'from' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />} Da, șterge
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setConfirmFrom(false)} disabled={busy === 'from'}>Nu</Button>
                    </div>
                  ) : (
                    <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" disabled={!fromDate}
                      onClick={() => setConfirmFrom(true)} data-testid="from-date-btn">
                      <Trash2 className="w-4 h-4 mr-1" /> Șterge de la data aleasă
                    </Button>
                  )}
                </div>

                {confirmAll ? (
                  <div className="flex items-center justify-between gap-2 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                    <span className="text-sm text-red-700 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Ștergi toate încărcările lunii?</span>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => setConfirmAll(false)} disabled={busy === 'all'}>Anulează</Button>
                      <Button variant="destructive" size="sm" onClick={delAll} disabled={busy === 'all'} data-testid="import-delete-all-confirm">
                        {busy === 'all' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />} Da, șterge tot
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setConfirmAll(true)} data-testid="import-delete-all">
                    <Trash2 className="w-4 h-4 mr-1" /> Șterge toate încărcările (cantitate + Level)
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
});

export default MotivareImportsManager;
